'use client'
import { useState, useEffect } from "react"
import { Button } from "./button"
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion"
import { Menu, Mic, X } from 'lucide-react'
import { Link, useLocation } from "react-router-dom"
export default function NavBar() {
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const { scrollY } = useScroll()
  const navBackground = useTransform(
    scrollY,
    [0, 50],
    ["rgba(255, 255, 255, 0)", "rgba(255, 255, 255, 0.9)"]
  )
  const navPosition = useTransform(
    scrollY,
    [0, 50],
    ["translateY(0px)", "translateY(16px)"]
  )
  const textColor = useTransform(
    scrollY,
    [0, 50],
    ["rgba(255, 255, 255, 1)", "rgba(0, 0, 0, 1)"]
  )
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50)
    }
    window.addEventListener("scroll", handleScroll)
    return () => window.removeEventListener("scroll", handleScroll)
  }, [])
  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen)
  }
  return (
    <motion.div
      className="fixed top-0 left-0 right-0 z-50 px-4 py-2"
      style={{ transform: navPosition }}
    >
      <motion.header
        className="max-w-7xl mx-auto flex h-16 items-center justify-between rounded-full transition-all duration-300 ease-in-out px-6"
        style={{
          backgroundColor: navBackground,
          boxShadow: isScrolled ? "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)" : "none",
        }}
      >
        <motion.div style={{ color: textColor }}>
          <Link to="/" className="flex items-center space-x-2">
            <Mic className="h-6 w-6" style={{ color: '#002aff' }} />
            <span
              className={`font-bold text-xl ${
                location.pathname === '/pricing' ? 'text-black' : 'text-current'
              }`}
              style={{ fontFamily: 'Inria Serif, serif' }}
            >
              SpeakclearlyAI
            </span>
          </Link>
        </motion.div>
        
        {/* Desktop Navigation */}
        <motion.nav className="hidden md:flex items-center space-x-6" style={{ color: textColor }}>
          <Link
            to="/"
            className={`hover:underline text-md font-medium ${location.pathname === '/blogs' ? 'text-black' : 'text-current'}`}
          >
            Blogs
          </Link>
          <Link
            to="/pricing"
            className={`hover:underline text-md font-medium ${location.pathname === '/pricing' ? 'text-black' : 'text-current'}`}
          >
            Pricing
          </Link>
          <Link
            to="/extensions"
            className={`hover:underline text-md font-medium ${location.pathname === '/extensions' ? 'text-black' : 'text-current'}`}
          >
            Extensions
          </Link>
          <Link
            to="/#features"
            onClick={(e) => {
              if (location.pathname === '/') {
                e.preventDefault();
                const element = document.getElementById('features');
                if (element) {
                  element.scrollIntoView({ behavior: 'smooth' });
                }
              }
            }}
            className={`hover:underline text-md font-medium ${
              location.pathname === '/pricing' ? 'text-black' : 'text-current'
            }`}
          >
            Features
          </Link>
          <Link to="/auth">
            <Button
              variant="ghost"
              className={`text-lg font-medium ${location.pathname === '/pricing' ? 'text-black' : 'text-current'}`}
            >
              Login
            </Button>
          </Link>
        </motion.nav>
        {/* Mobile Menu Button */}
        <motion.div className="md:hidden" style={{ color: textColor }}>
          <Button
            variant="ghost"
            size="sm"
            onClick={toggleMobileMenu}
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? (
              <X className="h-5 w-5" />
            ) : (
              <Menu className="h-5 w-5" />
            )}
          </Button>
        </motion.div>
      </motion.header>
      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="md:hidden fixed top-20 inset-x-4 bg-white/95 backdrop-blur-sm rounded-2xl shadow-xl overflow-hidden z-50"
          >
            <div className="p-6">
              <nav className="flex flex-col space-y-6">
                <Link
                  to="/"
                  className="group relative"
                  onClick={toggleMobileMenu}
                >
                  <span className={`text-lg font-medium ${location.pathname === '/blogs' ? 'text-blue-600' : 'text-gray-800'}`}>
                    Blogs
                  </span>
                  <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-blue-600 transition-all group-hover:w-full" />
                </Link>
                
                <Link
                  to="/pricing"
                  className="group relative"
                  onClick={toggleMobileMenu}
                >
                  <span className={`text-lg font-medium ${location.pathname === '/pricing' ? 'text-blue-600' : 'text-gray-800'}`}>
                    Pricing
                  </span>
                  <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-blue-600 transition-all group-hover:w-full" />
                </Link>
                
                <Link
                  to="/extensions"
                  className="group relative"
                  onClick={toggleMobileMenu}
                >
                  <span className={`text-lg font-medium ${location.pathname === '/extensions' ? 'text-blue-600' : 'text-gray-800'}`}>
                    Extensions
                  </span>
                  <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-blue-600 transition-all group-hover:w-full" />
                </Link>
                
                <Link
                  to="/#features"
                  className="group relative"
                  onClick={(e) => {
                    toggleMobileMenu();
                    if (location.pathname === '/') {
                      e.preventDefault();
                      const element = document.getElementById('features');
                      if (element) {
                        element.scrollIntoView({ behavior: 'smooth' });
                      }
                    }
                  }}
                >
                  <span className={`text-lg font-medium text-gray-800`}>
                    Features
                  </span>
                  <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-blue-600 transition-all group-hover:w-full" />
                </Link>
                
                <div className="pt-4 border-t border-gray-200">
                  <Link to="/auth" onClick={toggleMobileMenu}>
                    <Button
                      className="w-full py-3 text-white bg-blue-600 hover:bg-blue-700 rounded-xl text-lg font-medium"
                    >
                      Login
                    </Button>
                  </Link>
                </div>
              </nav>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  )
}